import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuthStore } from '../../stores/useAuthStore';
import { supabase } from '../../lib/supabase';
import { fechaEnBogota } from '../../lib/cierre/bogotaDate';
import {
  getGastosPeriodo,
  getIngresosPeriodo,
  getTopGastosCategoria,
  getTopLineasFactura,
} from '../../lib/cierre/queries';
import { aggregateToBalance, computeSaldoFinal, formatCOP, totalBalance } from '../../lib/cierre/calculations';
import { emptyBalance } from '../../lib/cierre/types';
import { downloadCierrePdf } from './cierrePdf';

function monthBounds(y: number, mo: number): { desde: string; hasta: string } {
  const desde = `${y}-${String(mo).padStart(2, '0')}-01`;
  const last = new Date(y, mo, 0).getDate();
  const hasta = `${y}-${String(mo).padStart(2, '0')}-${String(last).padStart(2, '0')}`;
  return { desde, hasta };
}

export const CierreMensualView: React.FC = () => {
  const { currentEmpresa } = useAuthStore();
  const empresaId = currentEmpresa?.id;
  const bogota = fechaEnBogota();
  const [y0, m0] = useMemo((): [number, number] => {
    const [y, m] = bogota.split('-').map(Number);
    return [y || new Date().getFullYear(), m && m >= 1 && m <= 12 ? m : new Date().getMonth() + 1];
  }, [bogota]);

  const [anio, setAnio] = useState<number>(y0);
  const [mes, setMes] = useState<number>(m0);
  const [ingresos, setIngresos] = useState(emptyBalance());
  const [gastos, setGastos] = useState(emptyBalance());
  const [topProd, setTopProd] = useState<{ nombre: string; total: number }[]>([]);
  const [topCat, setTopCat] = useState<{ categoria: string; total: number }[]>([]);
  const [prevNet, setPrevNet] = useState<number | null>(null);
  const [thisNet, setThisNet] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const { desde, hasta } = useMemo(() => monthBounds(anio, mes), [anio, mes]);

  const load = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    setErr(null);
    try {
      const prevMo = mes === 1 ? 12 : mes - 1;
      const prevY = mes === 1 ? anio - 1 : anio;
      const pb = monthBounds(prevY, prevMo);

      const [ingLines, gasLines, prod, cat, ingPrev, gasPrev] = await Promise.all([
        getIngresosPeriodo(supabase, empresaId, desde, hasta),
        getGastosPeriodo(supabase, empresaId, desde, hasta),
        getTopLineasFactura(supabase, empresaId, desde, hasta, 12),
        getTopGastosCategoria(supabase, empresaId, desde, hasta, 10),
        getIngresosPeriodo(supabase, empresaId, pb.desde, pb.hasta),
        getGastosPeriodo(supabase, empresaId, pb.desde, pb.hasta),
      ]);

      const ing = aggregateToBalance(ingLines);
      const gas = aggregateToBalance(gasLines);
      setIngresos(ing);
      setGastos(gas);
      setTopProd(prod);
      setTopCat(cat);

      const ingP = aggregateToBalance(ingPrev);
      const gasP = aggregateToBalance(gasPrev);
      const nThis = totalBalance(ing) - totalBalance(gas);
      const nPrev = totalBalance(ingP) - totalBalance(gasP);
      setThisNet(nThis);
      setPrevNet(nPrev);
    } catch (e) {
      console.error(e);
      setErr(e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [empresaId, desde, hasta, anio, mes]);

  useEffect(() => {
    void load();
  }, [load]);

  const netoPorMetodo = useMemo(
    () => computeSaldoFinal(emptyBalance(), ingresos, gastos),
    [ingresos, gastos]
  );

  const deltaPct =
    prevNet != null && prevNet !== 0 && thisNet != null
      ? Math.round(((thisNet - prevNet) / Math.abs(prevNet)) * 1000) / 10
      : null;

  if (!empresaId) return <p className="text-gray-600">Selecciona una empresa.</p>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#0B1F4A]">Cierre mensual</h1>
          <p className="text-sm text-gray-600">Agregados en facturas y gastos (COP, sin decimales).</p>
        </div>
        <div className="flex gap-2">
          <label className="text-sm">
            Año
            <input
              type="number"
              className="ml-1 w-24 rounded border border-gray-300 px-2 py-1"
              value={anio}
              onChange={(e) => setAnio(parseInt(e.target.value, 10) || anio)}
            />
          </label>
          <label className="text-sm">
            Mes
            <select
              className="ml-1 rounded border border-gray-300 px-2 py-1"
              value={mes}
              onChange={(e) => setMes(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {m.toString().padStart(2, '0')}
                </option>
              ))}
            </select>
          </label>
        </div>
        <button
          type="button"
          onClick={() =>
            downloadCierrePdf({
              titulo: 'Mensual',
              periodo: `${anio}-${String(mes).padStart(2, '0')}`,
              empresaNombre: currentEmpresa?.nombre || 'Empresa',
              ingresos,
              gastos,
              netoPorMetodo,
              extraLines: [
                `Neto mes: ${thisNet != null ? formatCOP(thisNet) : '—'}`,
                prevNet != null ? `Neto mes anterior: ${formatCOP(prevNet)}` : '',
                deltaPct != null ? `Variación: ${deltaPct}%` : '',
              ].filter(Boolean),
            })
          }
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800"
        >
          Exportar PDF
        </button>
      </div>

      {err ? <div className="rounded bg-red-50 p-3 text-red-800">{err}</div> : null}
      {loading ? <p className="text-gray-500">Cargando…</p> : null}

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-2 font-semibold text-gray-900">Ingresos por método</h2>
          <ul className="space-y-1 text-sm">
            {Object.entries(
              ingresos as Record<string, number>
            ).map(([k, v]) =>
              v ? (
                <li key={k} className="flex justify-between tabular-nums">
                  <span className="capitalize">{k}</span>
                  <span>{formatCOP(v)}</span>
                </li>
              ) : null
            )}
          </ul>
          <p className="mt-2 font-bold">Total ingresos: {formatCOP(totalBalance(ingresos))}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-2 font-semibold text-gray-900">Gastos por método</h2>
          <ul className="space-y-1 text-sm">
            {Object.entries(gastos as Record<string, number>).map(([k, v]) =>
              v ? (
                <li key={k} className="flex justify-between tabular-nums">
                  <span className="capitalize">{k}</span>
                  <span>{formatCOP(v)}</span>
                </li>
              ) : null
            )}
          </ul>
          <p className="mt-2 font-bold">Total gastos: {formatCOP(totalBalance(gastos))}</p>
        </div>
      </section>

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <h2 className="mb-2 font-semibold text-gray-900">Comparativa vs mes anterior</h2>
        <p className="text-sm text-gray-700">
          Neto este mes: <strong>{thisNet != null ? formatCOP(thisNet) : '—'}</strong>
        </p>
        <p className="text-sm text-gray-700">
          Neto mes anterior: <strong>{prevNet != null ? formatCOP(prevNet) : '—'}</strong>
        </p>
        {deltaPct != null ? (
          <p className={`text-sm font-semibold ${deltaPct >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
            Variación: {deltaPct >= 0 ? '↑' : '↓'} {Math.abs(deltaPct)}%
          </p>
        ) : null}
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-2 font-semibold text-gray-900">Top líneas de factura</h2>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {topProd.map((r) => (
              <li key={r.nombre} className="flex justify-between gap-2">
                <span className="truncate">{r.nombre}</span>
                <span className="shrink-0 tabular-nums">{formatCOP(r.total)}</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-2 font-semibold text-gray-900">Top gastos por categoría</h2>
          <ol className="list-decimal space-y-1 pl-5 text-sm">
            {topCat.map((r) => (
              <li key={r.categoria} className="flex justify-between gap-2">
                <span className="truncate">{r.categoria}</span>
                <span className="shrink-0 tabular-nums">{formatCOP(r.total)}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
    </div>
  );
};
